import React, { useCallback, useMemo } from 'react';
import { useTranslations } from '@components/context';
import { AUTOMATION_OPERATOR_AND, AUTOMATION_OPERATOR_OR } from '../conditionFormHelpers';
import { createEmptyConditionGroup } from '@stores/positionAutomation/PositionAutomation.state';
import Button from '@components/legacy/core/Button';
import ConditionGroup, { ConditionOperatorSwitch } from './ConditionGroup';

const isSelectableConditionOption = (option) => (
    option.enum.length > 0
    || (option.apply_default_value && option.default_value.length > 0)
);

const AutomationConditionsEditor = ({
    configs,
    event,
    groups,
    onChangeGroups,
    topOperator,
    onTopOperatorChange,
    readOnly = false,
}) => {
    const { t } = useTranslations();

    const translate = useCallback((key) => {
        return configs?.translations?.[key] || key;
    }, [configs?.translations]);

    const conditionOptions = useMemo(() => {
        if (!configs?.conditions || !event) {
            return [];
        }
        return Object.entries(configs.conditions)
            .filter(([, entry]) => Array.isArray(entry.events) && entry.events.includes(event))
            .flatMap(([, entry]) =>
                Object.entries(entry.filters).map(([filterId, filter]) => ({
                    id: filterId,
                    text: entry.apply_default_value ? (entry.title || '') : (filter?.label || translate(filterId)),
                    enum: filter?.enum || [],
                    default_value: filter?.default_value || [],
                    allowed_multiple: entry.allowed_multiple,
                    apply_default_value: Boolean(entry.apply_default_value),
                }))
            );
    }, [configs?.conditions, event, translate]);

    const selectableConditionOptions = useMemo(
        () => conditionOptions.filter(isSelectableConditionOption),
        [conditionOptions],
    );

    const operatorOptions = useMemo(() => [
        { id: AUTOMATION_OPERATOR_AND, label: t('AND') },
        { id: AUTOMATION_OPERATOR_OR, label: t('OR') },
    ], [t]);

    const groupList = Array.isArray(groups) ? groups : [];

    const updateGroup = (index, nextGroup) => {
        onChangeGroups(groupList.map((group, i) => (i === index ? nextGroup : group)));
    };
    const removeGroup = (index) => {
        onChangeGroups(groupList.filter((_, i) => i !== index));
    };
    const addGroup = () => {
        onChangeGroups([...groupList, createEmptyConditionGroup()]);
    };

    return (
        <>
            <div className="auto-sched-section-title">{t('Given these conditions...')}</div>
            {groupList.map((group, index) => (
                <React.Fragment key={group.groupKey ?? index}>
                    {index > 0 ? (
                        <ConditionOperatorSwitch
                            value={topOperator}
                            onChange={onTopOperatorChange}
                            ariaLabel={t('Combine groups')}
                            t={t}
                            readOnly={readOnly}
                            options={operatorOptions}
                        />
                    ) : null}
                    <ConditionGroup
                        event={event}
                        group={group}
                        conditionOptions={conditionOptions}
                        selectableConditionOptions={selectableConditionOptions}
                        operatorOptions={operatorOptions}
                        onChange={(nextGroup) => updateGroup(index, nextGroup)}
                        onRemoveGroup={() => removeGroup(index)}
                        canRemoveGroup
                        readOnly={readOnly}
                    />
                </React.Fragment>
            ))}

            {!readOnly ? (
                <Button
                    type="link"
                    noPadding
                    className="auto-sched-criteria-add"
                    icon="icon-svg-plus"
                    disabled={!event}
                    onClick={() => { event && addGroup(); }}
                >
                    {groupList.length === 0 ? t('Add condition') : t('Add another group')}
                </Button>
            ) : null}
        </>
    );
};

export default AutomationConditionsEditor;
